import { Component, EventEmitter, Inject, Input, OnInit, Optional, Output } from '@angular/core';
import {
  COORDINATOR_ROLE,
  CUSTOMER_CARER_ROLE,
  CUSTOMER_FAMILY_REPRESENTATIVE_ROLE,
  CUSTOMER_FAMILY_ROLE,
  CUSTOMER_SELF_ROLE,
  DASHBOARD_ROLES,
  ORGANISATION_ADMIN_ROLE,
} from '@careapp-group/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { requestDownloadUrl } from '../../store';
import { FilesSelectors } from '../../store/files/files-selectors';
import { filter, take } from 'rxjs/operators';
import { FileRecordDto } from '@careapp-group/ng-sdk';
import { parseISO } from 'date-fns';
import * as actions from '../../store/actions';
import { MatDialog } from '@angular/material/dialog';
import { SelectRoleDialogComponent } from '../select-role-dialog/select-role-dialog.component';
import { CA_ENVIRONMENT, WINDOW } from '../../app.di';

@Component({
  styleUrls: ['./file-card.component.scss'],
  templateUrl: './file-card.component.html',
  selector: 'ca-file-card',
  standalone: false,
})
export class FileCardComponent implements OnInit {
  DASHBOARD_ROLES = DASHBOARD_ROLES;
  NULL_ROLES_VISIBILITY = [ORGANISATION_ADMIN_ROLE, COORDINATOR_ROLE, CUSTOMER_CARER_ROLE];

  @Output() setVisibility = new EventEmitter();
  @Output() remove = new EventEmitter();
  @Output() error = new EventEmitter();
  @Output() viewHistory = new EventEmitter();

  public fileUrl$: Observable<string>;

  private _loading: boolean;
  get loading(): boolean {
    return this._loading;
  }
  private _file: FileRecordDto;
  public get file(): FileRecordDto {
    return this._file;
  }
  @Input()
  public set file(file: FileRecordDto) {
    this._file = file;
    this.id = file?.id;
    this.type = file?.mimeType;
    this.name = file?.originalFileName;
    this.size = file?.size;
    this.created = parseISO(file?.created);
  }
  @Input()
  set loading(value: boolean) {
    this._loading = value;
    // Only request the URL when loading is complete.
    if (!value) {
      this.requestUrl();
    }
  }

  ngOnInit() {
    // Waits until both the file and loading properties are set.
    this.requestUrl();
  }

  // Requests a download URL for the current file. Only applies to files associated
  // with modules that have had permissions configured in v2 (Nest).
  requestUrl() {
    if ((this.file?.id ?? false) && !this.loading) {
      this.store.dispatch(requestDownloadUrl({ id: this.file.id }));
      this.fileUrl$ = this.store.select(FilesSelectors.getCurrentUrl(this.file.id));
    }
  }

  @Input() showActions: boolean;
  @Input() showVisibility: boolean;

  @Input() id: string;
  @Input() type: string;
  @Input() name: string;
  @Input() size: number;
  @Input() created: Date;

  @Input() openable: boolean;
  @Input() roles: string[];

  openFile() {
    if (this.openable && !this.loading && this.window) {
      this.store.dispatch(
        actions.requestLogFileView({
          fileId: this.file?.id,
        }),
      );

      this.fileUrl$
        .pipe(
          filter((url) => !!url),
          take(1),
        )
        .subscribe((url) => this.window.open(url, '_blank'));
    }
  }

  constructor(
    private store: Store<any>,
    @Optional() @Inject(WINDOW) private window: Window,
    private dialog: MatDialog,
    @Inject(CA_ENVIRONMENT) private environment,
  ) {}

  get visibleToFamily() {
    return this.roles?.includes(CUSTOMER_FAMILY_ROLE) ?? false;
  }
  get visibleToCustomer() {
    return this.roles?.includes(CUSTOMER_SELF_ROLE) ?? false;
  }

  updateRoles() {
    let currentRoles = this.roles ?? this.NULL_ROLES_VISIBILITY;

    const dialogRef = this.dialog.open(SelectRoleDialogComponent, {
      width: '500px',
      data: {
        selectedRoles: currentRoles,
        disabledRoles: [ORGANISATION_ADMIN_ROLE, COORDINATOR_ROLE],
        roleOptions: this.environment.documents.shareToFamilyRepresentatives
          ? [
              ORGANISATION_ADMIN_ROLE,
              COORDINATOR_ROLE,
              CUSTOMER_CARER_ROLE,
              CUSTOMER_SELF_ROLE,
              CUSTOMER_FAMILY_REPRESENTATIVE_ROLE,
            ]
          : [
              ORGANISATION_ADMIN_ROLE,
              COORDINATOR_ROLE,
              CUSTOMER_CARER_ROLE,
              CUSTOMER_SELF_ROLE,
              CUSTOMER_FAMILY_ROLE,
            ],
      },
    });

    dialogRef.afterClosed().subscribe((dialogRoles: string[]) => {
      if (!dialogRoles) {
        return;
      }

      this.setVisibility.emit(dialogRoles);
    });
  }
}
